import type { Condition, ConditionCategory, ConditionStatus, TeamMember } from '../types/conditions';

export const TODAY = '2026-10-12';
export const CURRENT_USER_ID = 'nd';
export const BORROWER_NAME = 'Michael & Sarah Johnson';

export const categories: { id: ConditionCategory; label: string }[] = [
  { id: 'processor', label: 'Processor' },
  { id: 'admin', label: 'Admin' },
  { id: 'title', label: 'Title' },
];

/** Order matches the Salesforce status picklist. */
export const statusOptions: { value: ConditionStatus; label: string }[] = [
  { value: 'none', label: '--NONE--' },
  { value: 'new', label: 'NEW' },
  { value: 'requested', label: 'REQUESTED' },
  { value: 'cleared', label: 'CLEARED' },
  { value: 'waived', label: 'WAIVED' },
  { value: 'review', label: 'REVIEW' },
];

/** Statuses that move a condition into the Cleared section. */
export const resolvedStatuses: ConditionStatus[] = ['cleared', 'waived'];

export const teamMembers: TeamMember[] = [
  { id: 'nd', name: 'Nicole Davis', initials: 'ND', role: 'Processor', color: '#3B3E43' },
  { id: 'mc', name: 'Marcus Chen', initials: 'MC', role: 'Processing Admin', color: '#1F64AD' },
  { id: 'pp', name: 'Priya Patel', initials: 'PP', role: 'Title Coordinator', color: '#4F8FD0' },
  { id: 'db', name: 'Dana Brooks', initials: 'DB', role: 'AVP of Processing', color: '#0F3A66' },
];

export const conditions: Condition[] = [
  {
    id: 'c1',
    category: 'processor',
    name: 'Vesting',
    description: 'Confirm how vesting will be held.',
    status: 'requested',
    createdAt: '2026-06-24',
    requestedAt: '2026-07-13',
    eta: '2026-10-08',
    assigneeId: 'nd',
    documents: 0,
    includeInEmail: true,
    borrowerVisible: true,
    comments: [
      {
        id: 'cm1',
        authorId: 'nd',
        body: 'Borrowers want joint tenancy with right of survivorship. Waiting on signed vesting form. Marcus, can you confirm the form went out in the portal?',
        createdAt: '2026-10-09T14:14:00',
        mentions: ['mc'],
      },
      {
        id: 'cm2',
        authorId: 'mc',
        body: 'Confirmed — portal link sent this morning.',
        createdAt: '2026-10-09T15:02:00',
        mentions: [],
      },
      {
        id: 'cm2b',
        authorId: 'nd',
        body: 'Hi Michael and Sarah — please sign the vesting form in your portal so we can keep your closing on track. Let us know if you have any questions.',
        createdAt: '2026-10-09T15:20:00',
        mentions: [],
        sentToBorrower: true,
      },
    ],
  },
  {
    id: 'c2',
    category: 'processor',
    name: 'Hazard Insurance',
    description: 'Provide evidence of homeowners insurance with lender as mortgagee.',
    status: 'new',
    createdAt: '2026-10-09',
    assigneeId: 'nd',
    documents: 0,
    includeInEmail: true,
    borrowerVisible: true,
    comments: [],
  },
  {
    id: 'c3',
    category: 'processor',
    name: 'Bank Statements',
    description: 'Two most recent months, all pages, for account ending 4821.',
    status: 'review',
    createdAt: '2026-10-01',
    requestedAt: '2026-10-02',
    eta: '2026-10-09',
    assigneeId: 'nd',
    documents: 3,
    includeInEmail: false,
    borrowerVisible: true,
    comments: [
      {
        id: 'cm3',
        authorId: 'nd',
        body: 'Statements uploaded, reviewing large deposit on 9/14.',
        createdAt: '2026-10-10T09:30:00',
        mentions: [],
      },
    ],
  },
  {
    id: 'c4',
    category: 'processor',
    name: 'Letter of Explanation',
    description: 'Explain credit inquiry from 08/22/2026.',
    status: 'review',
    createdAt: '2026-10-10',
    requestedAt: '2026-10-10',
    assigneeId: 'nd',
    documents: 1,
    includeInEmail: false,
    borrowerVisible: false,
    comments: [],
  },
  {
    id: 'c5',
    category: 'processor',
    name: 'Verification of Employment',
    description: 'Written VOE from current employer.',
    status: 'cleared',
    createdAt: '2026-09-22',
    requestedAt: '2026-09-23',
    clearedAt: '2026-10-06',
    assigneeId: 'nd',
    documents: 1,
    includeInEmail: false,
    borrowerVisible: true,
    comments: [],
  },
  {
    id: 'c6',
    category: 'processor',
    name: 'Appraisal',
    description: 'Full appraisal report reviewed and accepted.',
    status: 'waived',
    createdAt: '2026-09-18',
    requestedAt: '2026-09-18',
    clearedAt: '2026-10-02',
    assigneeId: 'nd',
    documents: 2,
    includeInEmail: false,
    borrowerVisible: false,
    comments: [],
  },
  {
    id: 'c7',
    category: 'admin',
    name: 'Initial Disclosures Signed',
    description: 'Confirm all borrowers e-signed initial LE package.',
    status: 'review',
    createdAt: '2026-10-05',
    requestedAt: '2026-10-05',
    eta: '2026-10-10',
    assigneeId: 'mc',
    documents: 1,
    includeInEmail: false,
    borrowerVisible: false,
    comments: [],
  },
  {
    id: 'c8',
    category: 'admin',
    name: 'Flood Certificate',
    description: 'Order and attach standard flood hazard determination.',
    status: 'cleared',
    createdAt: '2026-09-28',
    clearedAt: '2026-09-30',
    assigneeId: 'mc',
    documents: 1,
    includeInEmail: false,
    borrowerVisible: false,
    comments: [],
  },
  {
    id: 'c9',
    category: 'title',
    name: 'Title Commitment',
    description: 'Preliminary title commitment with Schedule B exceptions.',
    status: 'requested',
    createdAt: '2026-10-02',
    requestedAt: '2026-10-03',
    eta: '2026-10-16',
    assigneeId: 'pp',
    documents: 0,
    includeInEmail: true,
    borrowerVisible: false,
    comments: [
      {
        id: 'cm4',
        authorId: 'pp',
        body: 'Title company says commitment is with examiner, expect by Friday.',
        createdAt: '2026-10-08T11:20:00',
        mentions: [],
      },
    ],
  },
  {
    id: 'c10',
    category: 'title',
    name: 'Closing Protection Letter',
    description: 'CPL naming lender and settlement agent.',
    status: 'new',
    createdAt: '2026-10-08',
    assigneeId: 'pp',
    documents: 0,
    includeInEmail: true,
    borrowerVisible: false,
    comments: [],
  },
  {
    id: 'c11',
    category: 'title',
    name: 'Tax Certificate',
    description: 'Current year property tax status.',
    status: 'cleared',
    createdAt: '2026-09-25',
    clearedAt: '2026-10-01',
    assigneeId: 'pp',
    documents: 1,
    includeInEmail: false,
    borrowerVisible: false,
    comments: [],
  },
];
